import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Timestamp } from '../components/ui/Timestamp'
import { Capacitor } from '@capacitor/core'
import {
  MapPin,
  Plus,
  Trash2,
  Pencil,
  Navigation,
  Link as LinkIcon,
  MapPinned,
  Upload,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/Dialog'
import { EmptyState } from '../components/ui/EmptyState'
import { useToast } from '../components/ui/Toast'
import { placeService, placesBoard, type Place, type PlaceInput, type PlaceWithWaiting } from '../services/placeService'
import { sectionPlaces, waitingLine, placesSummary, matchesQuery } from '../utils/placeSections'
import { geofenceSyncService } from '../services/geofenceSyncService'
import { HortonGeofence, isGeofenceSupported } from '../services/geofencePlugin'
import { LocationPermissionPrimer } from '../components/places/LocationPermissionPrimer'
import { ImportGoogleModal } from '../components/places/ImportGoogleModal'
import { PlaceMapPreview, googleMapsSearchUrl } from '../components/places/PlaceMapPreview'
import { MediaStrip } from '../components/media/MediaStrip'
import { PlacePostcard } from '../components/diary/PlacePostcard'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'

type EntryMode = 'current' | 'manual' | 'maps_link'

interface DraftPlace {
  title: string
  address: string
  latitude: string
  longitude: string
  radius_m: number
  tags: string
}

const EMPTY_DRAFT: DraftPlace = {
  title: '',
  address: '',
  latitude: '',
  longitude: '',
  radius_m: 150,
  tags: '',
}

async function getCurrentCoords(): Promise<{ lat: number; lng: number }> {
  // Prefer the native Capacitor plugin; fall back to browser Geolocation API.
  if (Capacitor.isNativePlatform()) {
    try {
      // @ts-ignore optional native-only dependency
      const { Geolocation } = await import('@capacitor/geolocation')
      const perm = await Geolocation.requestPermissions()
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
        throw new Error('Location permission denied')
      }
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 8000,
      })
      return { lat: pos.coords.latitude, lng: pos.coords.longitude }
    } catch (err) {
      throw err instanceof Error ? err : new Error(String(err))
    }
  }
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Browser does not support geolocation'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.message || 'Failed to get location')),
      { enableHighAccuracy: true, timeout: 8000 },
    )
  })
}

function CoordsMapPreview({
  latStr,
  lngStr,
  label,
  heightPx = 200,
}: {
  latStr: string
  lngStr: string
  label?: string
  heightPx?: number
}): React.JSX.Element | null {
  const lat = parseFloat(latStr)
  const lng = parseFloat(lngStr)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return (
    <PlaceMapPreview
      latitude={lat}
      longitude={lng}
      label={label}
      heightPx={heightPx}
    />
  )
}

/**
 * A pasted Maps link fills the draft in ONE step: coordinates always, and
 * the place name from the URL when the title is still empty ("not able to
 * add a place" 2026-08-23: the link carried "Trader Joe's" and coordinates,
 * yet Save stayed disabled until both were re-entered by hand). Returns null
 * when the link holds no coordinates so callers can keep the draft as-is.
 */
function draftFromMapsLink(link: string, d: DraftPlace): DraftPlace | null {
  const parsed = placeService.parseMapsLink(link)
  if (!parsed || parsed.lat == null || parsed.lng == null) return null
  const name = placeService.parsePlaceName(link)
  return {
    ...d,
    latitude: parsed.lat.toFixed(6),
    longitude: parsed.lng.toFixed(6),
    title: d.title.trim() ? d.title : (name ?? d.title),
  }
}

/** Why Save is disabled, said out loud instead of a dead grey button. */
function missingForSave(d: DraftPlace): string | null {
  const hasTitle = d.title.trim().length > 0
  const hasCoords =
    Number.isFinite(parseFloat(d.latitude)) &&
    Number.isFinite(parseFloat(d.longitude))
  if (hasTitle && hasCoords) return null
  if (!hasTitle && !hasCoords) return 'Needs a title and coordinates'
  return hasTitle ? 'Needs coordinates — use an option above' : 'Needs a title'
}

function PlaceFormFields({
  mode,
  setMode,
  draft,
  setDraft,
  mapsLink,
  setMapsLink,
  onParseLink,
  onFetchCurrent,
  coordsBusy,
  autoFocusTitle = false,
}: {
  mode: EntryMode
  setMode: (m: EntryMode) => void
  draft: DraftPlace
  setDraft: React.Dispatch<React.SetStateAction<DraftPlace>>
  mapsLink: string
  setMapsLink: (s: string) => void
  onParseLink: () => void
  onFetchCurrent: () => void
  coordsBusy: boolean
  autoFocusTitle?: boolean
}): React.JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={mode === 'current' ? 'default' : 'outline'}
          onClick={() => setMode('current')}
        >
          <Navigation className="h-4 w-4 mr-1.5" />
          Use current
        </Button>
        <Button
          size="sm"
          variant={mode === 'manual' ? 'default' : 'outline'}
          onClick={() => setMode('manual')}
        >
          <MapPinned className="h-4 w-4 mr-1.5" />
          Manual
        </Button>
        <Button
          size="sm"
          variant={mode === 'maps_link' ? 'default' : 'outline'}
          onClick={() => setMode('maps_link')}
        >
          <LinkIcon className="h-4 w-4 mr-1.5" />
          Maps link
        </Button>
      </div>

      <label className="block text-sm font-medium text-primary">
        Title
        <Input
          autoFocus={autoFocusTitle}
          placeholder="Trader Joe's on 5th"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
      </label>

      <label className="block text-sm font-medium text-primary">
        Address (optional)
        <Input
          placeholder="123 Main St"
          value={draft.address}
          onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
        />
      </label>

      {mode === 'current' && (
        <div className="rounded-md border border-soft p-3 bg-surface-alt">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-secondary">
              {draft.latitude && draft.longitude
                ? `${draft.latitude}, ${draft.longitude}`
                : 'Tap to read device GPS'}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={onFetchCurrent}
              disabled={coordsBusy}
            >
              {coordsBusy ? 'Reading…' : 'Read location'}
            </Button>
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-sm font-medium text-primary">
            Latitude
            <Input
              inputMode="decimal"
              placeholder="37.7749"
              value={draft.latitude}
              onChange={(e) =>
                setDraft((d) => ({ ...d, latitude: e.target.value }))
              }
            />
          </label>
          <label className="text-sm font-medium text-primary">
            Longitude
            <Input
              inputMode="decimal"
              placeholder="-122.4194"
              value={draft.longitude}
              onChange={(e) =>
                setDraft((d) => ({ ...d, longitude: e.target.value }))
              }
            />
          </label>
        </div>
      )}

      {mode === 'maps_link' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-primary">
            Paste a Google Maps link — name and coordinates fill in automatically
            <Input
              placeholder="https://www.google.com/maps/place/Trader+Joe's/@37.60,-122.39,17z"
              value={mapsLink}
              onChange={(e) => setMapsLink(e.target.value)}
            />
          </label>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={onParseLink}>
              Extract from link
            </Button>
            {draft.latitude && draft.longitude && (
              <span className="text-xs text-tertiary">
                {draft.latitude}, {draft.longitude}
              </span>
            )}
          </div>
        </div>
      )}

      <CoordsMapPreview
        latStr={draft.latitude}
        lngStr={draft.longitude}
        label="Preview for coordinates above"
        heightPx={200}
      />

      <div>
        <label className="block text-sm font-medium text-primary">
          Trigger radius: <span className="text-brand">{draft.radius_m}m</span>
        </label>
        <input
          type="range"
          min={50}
          max={500}
          step={10}
          value={draft.radius_m}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              radius_m: parseInt(e.target.value, 10),
            }))
          }
          className="w-full accent-brand"
        />
        <div className="flex justify-between text-xs text-tertiary">
          <span>50m</span>
          <span>500m</span>
        </div>
      </div>

      <label className="block text-sm font-medium text-primary">
        Tags (comma separated, optional)
        <Input
          placeholder="grocery, errand"
          value={draft.tags}
          onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))}
        />
      </label>
    </div>
  )
}

function PlaceCard({
  place,
  onEdit,
  onDelete,
}: {
  place: PlaceWithWaiting
  onEdit: (place: Place) => void
  onDelete: (id: number) => void
}): React.JSX.Element {
  const { isDiary } = useTheme()

  // Diary theme: the untheme-able Google iframe gives way to an animated
  // postcard scene. Same edit/delete, and the real map stays one tap away.
  if (isDiary) {
    return (
      <PlacePostcard
        title={place.title}
        address={place.address}
        tags={place.tags ?? []}
        latitude={place.latitude}
        longitude={place.longitude}
        radiusM={place.radius_m}
        corner={
          /* Touch targets (2026-08-29): Delete sat flush against Edit. */
          <div className="flex shrink-0 gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${place.title}`}
              onClick={() => onEdit(place)}
            >
              <Pencil className="h-4 w-4" style={{ color: 'var(--ink-faded)' }} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="ml-2"
              aria-label={`Delete ${place.title}`}
              onClick={() => onDelete(place.id)}
            >
              <Trash2 className="h-4 w-4" style={{ color: 'var(--ink-faded)' }} />
            </Button>
          </div>
        }
        footer={
          <a
            href={googleMapsSearchUrl(place.latitude, place.longitude)}
            target="_blank"
            rel="noreferrer"
            className="yn-postmark inline-flex items-center gap-1 hover:underline"
          >
            <MapPinned className="w-3 h-3" /> open the real map
          </a>
        }
      />
    )
  }

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="mt-1 h-10 w-10 rounded-full bg-brand-subtle flex items-center justify-center text-brand shrink-0">
          <MapPin className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Recognition-first (2026-08-29): the place name is what the user
              scans for, so it holds body size + weight while the coordinates
              under it drop to the micro step. */}
          <div className="text-app-body font-semibold text-primary truncate">{place.title}</div>
          {place.address && (
            <div className="text-app-meta text-secondary truncate">{place.address}</div>
          )}
          {/* The page's question, answered on the row: what is waiting here. */}
          <div className="mt-1 text-app-micro text-secondary">{waitingLine(place)}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0 text-app-micro text-tertiary">
            <span>
              {place.latitude.toFixed(4)}, {place.longitude.toFixed(4)}
            </span>
            <span className="hidden sm:inline">·</span>
            <span>{place.radius_m}m radius</span>
            {/* Owner ask 2026-08-30: places showed no timestamp at all. When
                a place was saved matters — a geofence you added last week
                behaves differently to one added an hour ago. */}
            {place.created_at && (
              <>
                <span className="hidden sm:inline">·</span>
                <Timestamp value={place.created_at} mode="date" label="added" />
              </>
            )}
          </div>
          {place.tags && place.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {place.tags.map((t) => (
                <span
                  key={t}
                  className="text-app-micro rounded-full bg-surface-alt text-secondary px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Touch targets (2026-08-29): Delete sat flush against Edit. */}
        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Edit ${place.title}`}
            onClick={() => onEdit(place)}
          >
            <Pencil className="h-4 w-4 text-secondary" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="ml-2"
            aria-label={`Delete ${place.title}`}
            onClick={() => onDelete(place.id)}
          >
            <Trash2 className="h-4 w-4 text-secondary" />
          </Button>
        </div>
      </div>
      <PlaceMapPreview
        latitude={place.latitude}
        longitude={place.longitude}
        label={place.title}
        heightPx={176}
      />
      {/* The map says where it is; a photo says what it looks like when you
          arrive, which is the part that makes a place recognisable. */}
      <MediaStrip
        kind="place"
        ownerId={place.id}
        label={place.title}
        className="mt-3 border-t border-soft pt-3"
      />
    </Card>
  )
}

function AddPlaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (place: Place) => void
}): React.JSX.Element {
  const toast = useToast()
  // Persona name follows the active theme (2026-08-29): a literal "yanamas"
  // leaked the diary brand into the default HortonIO theme.
  const { assistant: personaName } = useBranding()
  const [mode, setMode] = useState<EntryMode>('current')
  const [draft, setDraft] = useState<DraftPlace>(EMPTY_DRAFT)
  const [mapsLink, setMapsLink] = useState('')
  const [coordsBusy, setCoordsBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      setDraft(EMPTY_DRAFT)
      setMapsLink('')
      setMode('current')
    }
  }, [open])

  const canSave =
    draft.title.trim().length > 0 &&
    Number.isFinite(parseFloat(draft.latitude)) &&
    Number.isFinite(parseFloat(draft.longitude))

  const fetchCurrent = useCallback(async () => {
    setCoordsBusy(true)
    try {
      const { lat, lng } = await getCurrentCoords()
      setDraft((d) => ({
        ...d,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      }))
      toast.success('Location captured')
    } catch (err) {
      toast.error((err as Error).message || 'Could not read location')
    } finally {
      setCoordsBusy(false)
    }
  }, [toast])

  // Pasting the link is enough — coordinates AND the place name land in the
  // draft as soon as the text contains them. The button stays as an explicit
  // retry with feedback for links that need it.
  const handleMapsLink = useCallback((s: string) => {
    setMapsLink(s)
    setDraft((d) => draftFromMapsLink(s, d) ?? d)
  }, [])

  const parseLink = useCallback(() => {
    let filled = false
    setDraft((d) => {
      const next = draftFromMapsLink(mapsLink, d)
      filled = next != null
      return next ?? d
    })
    if (filled) toast.success('Filled in from the link')
    else toast.error('Could not find coordinates in that link')
  }, [mapsLink, toast])

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      const payload: PlaceInput = {
        title: draft.title.trim(),
        address: draft.address.trim() || null,
        latitude: parseFloat(draft.latitude),
        longitude: parseFloat(draft.longitude),
        radius_m: draft.radius_m,
        tags: draft.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      }
      const created = await placeService.create(payload)
      onCreated(created)
      toast.success(`Added ${created.title}`)
      onOpenChange(false)
    } catch (err) {
      toast.error((err as Error).message || 'Could not save place')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add a place</DialogTitle>
          <DialogDescription>
            {personaName} can remind you about tasks the moment you walk in.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          <PlaceFormFields
            mode={mode}
            setMode={setMode}
            draft={draft}
            setDraft={setDraft}
            mapsLink={mapsLink}
            setMapsLink={handleMapsLink}
            onParseLink={parseLink}
            onFetchCurrent={fetchCurrent}
            coordsBusy={coordsBusy}
            autoFocusTitle
          />

          <div className="flex items-center justify-end gap-2 pt-2">
            {!canSave && !saving && (
              <span className="mr-auto text-xs text-tertiary">{missingForSave(draft)}</span>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              {saving ? 'Saving…' : 'Save place'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function EditPlaceDialog({
  open,
  place,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  place: Place | null
  onOpenChange: (open: boolean) => void
  onSaved: (place: Place) => void
}): React.JSX.Element {
  const toast = useToast()
  const [mode, setMode] = useState<EntryMode>('manual')
  const [draft, setDraft] = useState<DraftPlace>(EMPTY_DRAFT)
  const [mapsLink, setMapsLink] = useState('')
  const [coordsBusy, setCoordsBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !place) return
    setDraft({
      title: place.title,
      address: place.address || '',
      latitude: place.latitude.toFixed(6),
      longitude: place.longitude.toFixed(6),
      radius_m: place.radius_m,
      tags: (place.tags || []).join(', '),
    })
    setMapsLink('')
    setMode('manual')
  }, [open, place])

  useEffect(() => {
    if (!open) {
      setDraft(EMPTY_DRAFT)
      setMapsLink('')
      setMode('manual')
    }
  }, [open])

  const fetchCurrent = useCallback(async () => {
    setCoordsBusy(true)
    try {
      const { lat, lng } = await getCurrentCoords()
      setDraft((d) => ({
        ...d,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6),
      }))
      toast.success('Location captured')
    } catch (err) {
      toast.error((err as Error).message || 'Could not read location')
    } finally {
      setCoordsBusy(false)
    }
  }, [toast])

  const handleMapsLink = useCallback((s: string) => {
    setMapsLink(s)
    setDraft((d) => draftFromMapsLink(s, d) ?? d)
  }, [])

  const parseLink = useCallback(() => {
    let filled = false
    setDraft((d) => {
      const next = draftFromMapsLink(mapsLink, d)
      filled = next != null
      return next ?? d
    })
    if (filled) toast.success('Filled in from the link')
    else toast.error('Could not find coordinates in that link')
  }, [mapsLink, toast])

  const canSave =
    !!place &&
    draft.title.trim().length > 0 &&
    Number.isFinite(parseFloat(draft.latitude)) &&
    Number.isFinite(parseFloat(draft.longitude))

  const handleSave = async () => {
    if (!canSave || !place) return
    setSaving(true)
    try {
      const updated = await placeService.update(place.id, {
        title: draft.title.trim(),
        address: draft.address.trim() || null,
        latitude: parseFloat(draft.latitude),
        longitude: parseFloat(draft.longitude),
        radius_m: draft.radius_m,
        google_place_id: place.google_place_id ?? null,
        tags: draft.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      })
      onSaved(updated)
      toast.success(`Updated ${updated.title}`)
      onOpenChange(false)
    } catch (err) {
      toast.error((err as Error).message || 'Could not update place')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open && !!place} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit place</DialogTitle>
          <DialogDescription>
            Change the name, coordinates, radius, or tags. The map preview updates
            as you edit lat/long.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4">
          <PlaceFormFields
            mode={mode}
            setMode={setMode}
            draft={draft}
            setDraft={setDraft}
            mapsLink={mapsLink}
            setMapsLink={handleMapsLink}
            onParseLink={parseLink}
            onFetchCurrent={fetchCurrent}
            coordsBusy={coordsBusy}
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            {!canSave && !saving && (
              <span className="mr-auto text-xs text-tertiary">{missingForSave(draft)}</span>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function Places(): React.JSX.Element {
  const toast = useToast()
  // Persona name follows the active theme (2026-08-29): a literal "yanamas"
  // leaked the diary brand into the default HortonIO theme.
  const { assistant: personaName } = useBranding()
  const [places, setPlaces] = useState<PlaceWithWaiting[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [editingPlace, setEditingPlace] = useState<Place | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [showPrimer, setShowPrimer] = useState(false)
  const [bgGranted, setBgGranted] = useState<boolean | null>(null)
  const [buildLabel, setBuildLabel] = useState<string | null>(null)

  // Check background-location status on mount + whenever the primer closes,
  // so the banner disappears the moment the user flips the toggle in Settings.
  const refreshPermState = useCallback(async () => {
    if (!isGeofenceSupported()) {
      setBgGranted(null)
      return
    }
    try {
      const r = await HortonGeofence.hasBackgroundPermission()
      setBgGranted(!!r.granted)
    } catch {
      setBgGranted(false)
    }
  }, [])

  useEffect(() => {
    void refreshPermState()
  }, [refreshPermState])

  // Load the APK build identifier so the user can confirm which version is
  // running after every fresh install. Falls back to web-build info off the
  // platform when the native plugin isn't available.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isGeofenceSupported()) {
        setBuildLabel(
          `web · ${import.meta.env.MODE}${
            import.meta.env.DEV ? ' (dev)' : ''
          }`,
        )
        return
      }
      try {
        const d = await HortonGeofence.getDiagnostics()
        if (cancelled) return
        const name = d.appVersionName || d.baseVersionName || '?'
        const code = d.appVersionCode ?? '?'
        setBuildLabel(`v${name} · build ${code}`)
      } catch {
        if (!cancelled) setBuildLabel('native · version unavailable')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The board carries what is waiting at each place — the page's whole
      // question. It degrades to the plain list on an older server.
      const list = await placesBoard()
      setPlaces(list)
    } catch (err) {
      setError((err as Error).message || 'Could not load places')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleCreated = useCallback(
    (p: Place) => {
      setPlaces((prev) => [p, ...prev])
      // Kick a geofence reconcile so the new place is armed immediately
      // if any existing tasks already reference it (unlikely on create,
      // but cheap and correct).
      void geofenceSyncService.reconcile()
    },
    [],
  )

  const handleUpdated = useCallback((p: Place) => {
    setPlaces((prev) => prev.map((x) => (x.id === p.id ? p : x)))
    void geofenceSyncService.reconcile()
  }, [])

  const handleDelete = useCallback(
    async (id: number) => {
      const prev = places
      setPlaces((list) => list.filter((p) => p.id !== id))
      try {
        await placeService.remove(id)
        void geofenceSyncService.reconcile()
        toast.success('Place removed')
      } catch (err) {
        setPlaces(prev)
        toast.error((err as Error).message || 'Could not remove place')
      }
    },
    [places, toast],
  )

  // Empty vs error (2026-08-29): this said "No saved places yet" on top of a
  // FAILED load. An empty state means we looked and there is nothing; a failed
  // request means we could not look, and it gets its own state below.
  const empty = useMemo(
    () => places.length === 0 && !loading && !error,
    [places, loading, error],
  )

  const shown = useMemo(
    () => places.filter((p) => matchesQuery(p, query)),
    [places, query],
  )
  const sections = useMemo(() => sectionPlaces(shown), [shown])
  const summary = useMemo(() => placesSummary(places), [places])

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-5 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <h1 className="text-lg font-bold text-primary">Places</h1>
          <p className="hidden md:block text-app-meta text-muted truncate">
            {summary}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-1.5" />
            Import from Google
          </Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add place
          </Button>
        </div>
      </div>

      {error && places.length > 0 && (
        <Card
          className="p-3 flex flex-wrap items-center gap-2 border-bad/40 text-app-body text-bad"
          role="alert"
        >
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => void load()}>
            Try again
          </Button>
        </Card>
      )}

      {isGeofenceSupported() && bgGranted === false && (
        <Card className="p-4 border-brand/40 bg-brand/5 flex items-start gap-3">
          <MapPin className="h-5 w-5 text-brand mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-primary mb-0.5">
              Location nudges are off
            </div>
            <div className="text-secondary">
              Enable background location so {personaName} can remind you the
              moment you walk into one of your saved places.
            </div>
          </div>
          <Button size="sm" onClick={() => setShowPrimer(true)}>
            Enable
          </Button>
        </Card>
      )}

      {loading ? (
        <Card className="p-8 text-center text-secondary">Loading places…</Card>
      ) : error && places.length === 0 ? (
        <Card className="p-8 text-center space-y-3" role="alert">
          <MapPin className="h-10 w-10 text-bad mx-auto" />
          <div className="text-app-body font-semibold text-primary">
            We could not load your places
          </div>
          <div className="text-app-meta text-secondary">{error}</div>
          <Button onClick={() => void load()}>Try again</Button>
        </Card>
      ) : empty ? (
        <EmptyState
          icon={<MapPin className="h-10 w-10 text-brand" />}
          title="No saved places yet"
          description="Add home, work, your favorite grocery store — then tag tasks to those places for walk-in reminders."
          actions={[
            {
              label: 'Add your first place',
              onClick: () => setShowAdd(true),
              icon: <Plus className="h-4 w-4 mr-1.5" />,
            },
          ]}
        />
      ) : (
        <div className="space-y-5">
          {/* Search appears only once there is enough to search through —
              a box over three places is furniture, not help. */}
          {places.length > 5 && (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search places, addresses, tags, or what is waiting"
              aria-label="Search places"
              data-testid="places-search"
              className="w-full rounded-lg border border-soft bg-surface px-3 py-2 text-app-body text-primary focus:border-brand focus:outline-none"
            />
          )}

          {sections.length === 0 ? (
            <Card className="p-6 text-center text-app-meta text-secondary">
              Nothing matches “{query}”.
            </Card>
          ) : (
            sections.map((section) => (
              <section key={section.id} data-testid={`places-section-${section.id}`}>
                <h2 className="text-app-body font-semibold text-primary">{section.title}</h2>
                <p className="mt-0.5 text-app-micro text-muted">{section.blurb}</p>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {section.places.map((p) => (
                    <PlaceCard
                      key={p.id}
                      place={p}
                      onEdit={setEditingPlace}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}

      <AddPlaceDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        onCreated={handleCreated}
      />

      <EditPlaceDialog
        open={editingPlace != null}
        place={editingPlace}
        onOpenChange={(v) => {
          if (!v) setEditingPlace(null)
        }}
        onSaved={handleUpdated}
      />

      <ImportGoogleModal
        open={showImport}
        onClose={() => setShowImport(false)}
        existingPlaces={places}
        onImported={() => {
          void load()
          void geofenceSyncService.reconcile()
        }}
      />

      <LocationPermissionPrimer
        open={showPrimer}
        onOpenChange={(v) => {
          setShowPrimer(v)
          if (!v) {
            void refreshPermState()
            void geofenceSyncService.reconcile()
          }
        }}
      />

      {buildLabel && (
        <div className="pt-2 text-center text-app-micro text-tertiary select-text">
          {buildLabel}
        </div>
      )}
    </div>
  )
}

export default Places
